/**
 * Stage 5: Publishing
 * Final stage - publish validated draft to production courses database
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseDraft } from '../../../types/creator-workflow';
import type { CoursePermission } from '../../../services/coursesService';
import { creatorWorkflowService } from '../../../services/creatorWorkflowService';
import { creatorGroupsService, type CreatorGroup } from '../../../services/creatorGroupsService';
import { coursesService } from '../../../services/coursesService';
import { BilingualTitle } from '../../common/BilingualTitle';
import { TagChipInput } from '../../common/TagChipInput';
import UnifiedAudioPlayer from '../../UnifiedAudioPlayer';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import './Stage5Publishing.css';

interface Stage5PublishingProps {
  draft: CourseDraft;
  loading: boolean;
  onGoBackStage?: () => Promise<void>;
}

/**
 * Clear all draft-related localStorage data after successful publish
 */
const clearDraftLocalStorage = (draftId: string) => {
  console.log('🧹 Clearing localStorage data for draft:', draftId);

  // Clear main draft backup
  localStorage.removeItem('creator_draft_backup');

  // Clear stage-specific backups
  localStorage.removeItem('stage1_form_backup');
  localStorage.removeItem('stage2_form_backup');

  // Clear all draft-specific edit backups
  Object.keys(localStorage)
    .filter(key => key.includes(draftId) || key.startsWith('stage2_edit_backup_'))
    .forEach(key => {
      localStorage.removeItem(key);
      console.log('  Removed:', key);
    });

  console.log('✅ localStorage cleared successfully');
}

const Stage5Publishing: React.FC<Stage5PublishingProps> = ({
  draft,
  loading,
  onGoBackStage,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishSuccess, setPublishSuccess] = useState<{courseId: string; message: string} | null>(null);

  // Publish settings state — default to private if copyrighted audio detected
  const [permission, setPermission] = useState<CoursePermission>(
    draft.is_copyrighted_audio ? 'private' : 'public'
  );
  const [whitelistInput, setWhitelistInput] = useState('');
  const [whitelistEmails, setWhitelistEmails] = useState<string[]>([]);
  const [useScheduledPublish, setUseScheduledPublish] = useState(false);
  const [scheduledPublishAt, setScheduledPublishAt] = useState('');

  // Group permission state
  const [creatorGroups, setCreatorGroups] = useState<CreatorGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Modal state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [thumbnailGenerating, setThumbnailGenerating] = useState(false);

  // Copyright acknowledgement — creator must affirm they own/are licensed before publishing.
  // Required for every draft, not only flagged ones: the detector misses plenty of cases
  // and the checkbox is our primary DMCA safe-harbor shield. Persisted via the
  // PATCH /drafts/:id/acknowledge-copyright endpoint so the timestamp survives reloads.
  const [copyrightAcknowledged, setCopyrightAcknowledged] = useState(
    Boolean(draft.copyright_acknowledged_at),
  );
  const [acknowledgeSaving, setAcknowledgeSaving] = useState(false);

  // Tag editor state — bound to draft.tags, autocomplete from existing course tags
  const [tags, setTags] = useState<string[]>(draft.tags || []);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  useEffect(() => {
    coursesService
      .listAllTags()
      .then(setTagSuggestions)
      .catch(() => setTagSuggestions([]));
  }, []);

  const handleTagsChange = async (next: string[]) => {
    setTags(next);
    try {
      await creatorWorkflowService.updateDraft(draft.id, { tags: next });
    } catch (err) {
      console.warn('Failed to save tags:', err);
    }
  };

  const handleAcknowledgeChange = async (checked: boolean) => {
    setCopyrightAcknowledged(checked);
    if (!checked) return;
    // Only persist when the user is affirming — un-checking is local UI state, since
    // an acknowledgement, once given, is the legal record and shouldn't be silently revoked.
    setAcknowledgeSaving(true);
    try {
      await creatorWorkflowService.acknowledgeCopyright(draft.id);
    } catch (err) {
      console.warn('Failed to persist copyright acknowledgement:', err);
      setCopyrightAcknowledged(false);
    } finally {
      setAcknowledgeSaving(false);
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    if (!showPublishModal) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPublishModal(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showPublishModal]);

  // Trigger thumbnail generation when publish modal opens
  const handleOpenPublishModal = () => {
    setShowPublishModal(true);
    if (!draft.thumbnail_url) {
      setThumbnailGenerating(true);
      creatorWorkflowService.generateThumbnail(draft.id)
        .then((result) => {
          console.log('Thumbnail generated:', result.thumbnailUrl);
        })
        .catch((err) => {
          console.warn('Thumbnail generation failed (non-blocking):', err);
        })
        .finally(() => setThumbnailGenerating(false));
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showPublishModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showPublishModal]);

  // Load creator's groups when permission is set to 'group'
  useEffect(() => {
    if (permission === 'group') {
      setGroupsLoading(true);
      creatorGroupsService.listGroups()
        .then((groups: CreatorGroup[]) => setCreatorGroups(groups))
        .catch((err: unknown) => console.error('Failed to load groups:', err))
        .finally(() => setGroupsLoading(false));
    }
  }, [permission]);

  const handleAddEmails = () => {
    const newEmails = whitelistInput
      .split(/[,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@') && !whitelistEmails.includes(e));
    if (newEmails.length > 0) {
      setWhitelistEmails(prev => [...prev, ...newEmails]);
      setWhitelistInput('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setWhitelistEmails(prev => prev.filter(e => e !== email));
  };

  // Get validation results from stage_progress
  const validationProgress = draft.stage_progress?.validation;
  const validationResult = validationProgress?.validation_result;
  const isValid = validationResult?.is_valid || false;
  const overallScore = validationResult?.overall_score || 0;
  const passedChecks = validationResult?.passed_checks || 0;
  const totalChecks = validationResult?.total_checks || 0;

  const handlePublish = async () => {
    setPublishing(true);
    setPublishError(null);
    setPublishSuccess(null);

    try {
      console.log('📤 Publishing draft:', draft.id);
      const publishOptions = {
        permission,
        whitelistEmails: permission === 'private' ? whitelistEmails : undefined,
        groupIds: permission === 'group' ? selectedGroupIds : undefined,
        scheduledPublishAt: useScheduledPublish && scheduledPublishAt ? scheduledPublishAt : undefined,
      };
      const result = await creatorWorkflowService.publishDraft(draft.id, publishOptions);

      console.log('✅ Publishing successful:', result);
      setPublishSuccess({
        courseId: result.courseId,
        message: result.message,
      });

      // Clear all draft-related localStorage data after successful publish
      clearDraftLocalStorage(draft.id);
    } catch (error: any) {
      console.error('❌ Publishing error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      setPublishError(errorMessage);
    } finally {
      setPublishing(false);
    }
  };

  // Compute audio URLs
  const audioGenProgress = draft.stage_progress?.audio_generation;
  const segmentedAudioData = audioGenProgress?.segmented_audio_data;
  const rawAudioUrl = segmentedAudioData?.merged_audio_url || (audioGenProgress as any)?.merged_audio_url || draft.audio_url || draft.uploaded_audio_url;
  const audioUrl = useMemo(() => rawAudioUrl ? resolveMediaUrl(rawAudioUrl) : undefined, [rawAudioUrl]);
  const subtitleUrlEn = segmentedAudioData?.subtitle_url_en || (audioGenProgress as any)?.subtitle_url_en || draft.subtitle_url_en;
  const subtitleUrlNative = segmentedAudioData?.subtitle_url_native || (audioGenProgress as any)?.subtitle_url_native || draft.subtitle_url_native;
  const mergedDuration = segmentedAudioData?.merged_duration_seconds || (audioGenProgress as any)?.merged_duration_seconds || 0;

  const wordCount = draft.actual_word_count || draft.sentences?.reduce((sum, s) => sum + (s.text?.split(/\s+/).filter(Boolean).length || 0), 0) || 0;

  const formatDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 85) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'fair';
    return 'poor';
  };

  return (
    <div className="stage-5-publishing">
      <header className="stage-header">
        <h2>{t('creator:workflow.stage5.title')}</h2>
        <p>{t('creator:workflow.stage5.description')}</p>
      </header>

      {/* Copyright Warning Banner */}
      {draft.is_copyrighted_audio && (
        <div className="copyright-warning-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <div>
            <strong>{t('creator:workflow.stage5.copyrightWarningTitle')}</strong>
            {draft.copyright_source ? (
              <p>
                {t('creator:workflow.stage5.copyrightWarningWithSource', { source: draft.copyright_source })}
              </p>
            ) : (
              <p>{t('creator:workflow.stage5.copyrightWarningDesc')}</p>
            )}
          </div>
        </div>
      )}

      {/* Validation Summary */}
      <div className="validation-summary-section">
        <h3>{t('creator:workflow.stage5.validationSummary')}</h3>
        <div className={`validation-score-card ${getScoreColor(overallScore)}`}>
          <div className="score-display">
            <div className="score-circle">
              <span className="score-value">{overallScore}</span>
              <span className="score-label">/100</span>
            </div>
            <div className="score-details">
              <h4>{isValid ? t('creator:workflow.stage5.readyToPublish') : t('creator:workflow.stage5.notReady')}</h4>
              <p>
                {t('creator:workflow.stage5.passedChecks', { passed: passedChecks, total: totalChecks })}
              </p>
              {(validationResult?.warnings_count ?? 0) > 0 && (
                <p className="warnings-info">
                  ⚠️ {validationResult?.warnings_count} warning(s)
                </p>
              )}
              {(validationResult?.recommendations_count ?? 0) > 0 && (
                <p className="recommendations-info">
                  💡 {validationResult?.recommendations_count} recommendation(s)
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Course Preview — styled like CourseDetailPage */}
      <div className="course-preview-section">
        <h3>{t('creator:workflow.stage5.coursePreview')}</h3>
        <div className="preview-card-v2">
          {/* Title */}
          <BilingualTitle
            title={draft.title || ''}
            titles={draft.titles}
            fallback={t('common:untitled')}
            variant="header"
            as="h1"
            className="preview-course-title"
          />
          {draft.description && (
            <p className="preview-course-description">{draft.description}</p>
          )}

          {/* Meta badges */}
          <div className="preview-meta-badges">
            {draft.cefr_level && (
              <span className="meta-badge difficulty">{draft.cefr_level}</span>
            )}
            {wordCount > 0 && (
              <span className="meta-badge">{wordCount} {t('creator:workflow.stage5.previewWordUnit')}</span>
            )}
            {mergedDuration > 0 && (
              <span className="meta-badge">{formatDuration(mergedDuration)}</span>
            )}
            {draft.content_type && (
              <span className="meta-badge">{draft.content_type}</span>
            )}
          </div>

          {/* Content stats */}
          <div className="preview-content-stats">
            <span className="stat-item">
              {draft.vocabulary?.length || 0} {t('creator:workflow.stage5.previewVocabulary')}
            </span>
            <span className="stat-separator">/</span>
            <span className="stat-item">
              {draft.sentences?.length || 0} {t('creator:workflow.stage5.previewSentences')}
            </span>
            <span className="stat-separator">/</span>
            <span className="stat-item">
              {audioUrl ? t('creator:workflow.stage5.audioReady') : t('creator:workflow.stage5.audioMissing')}
            </span>
            <span className="stat-separator">/</span>
            <span className="stat-item">
              {subtitleUrlEn ? t('creator:workflow.stage5.subtitlesReady') : t('creator:workflow.stage5.subtitlesMissing')}
            </span>
          </div>
        </div>

        {/* Audio Player */}
        {audioUrl && (
          <div className="preview-audio-section">
            <h4>{t('creator:workflow.stage5.listenPreview')}</h4>
            <UnifiedAudioPlayer
              audioUrl={audioUrl}
              subtitleUrlEn={subtitleUrlEn ? resolveMediaUrl(subtitleUrlEn) : undefined}
              subtitleUrlNative={subtitleUrlNative ? resolveMediaUrl(subtitleUrlNative) : undefined}
            />
          </div>
        )}
      </div>

      {/* Publishing Actions */}
      {!publishSuccess && !showPublishModal && (
        <div className="publishing-actions">
          {isValid && overallScore >= 70 ? (
            <div className="form-actions">
              <button
                className="btn-secondary"
                onClick={() => onGoBackStage?.()}
                disabled={publishing}
              >
                {t('creator:workflow.stage5.backToValidation')}
              </button>
              <button
                className="btn-success btn-large"
                onClick={handleOpenPublishModal}
                disabled={publishing || loading}
              >
                {t('creator:workflow.stage5.publishCourse')}
              </button>
            </div>
          ) : (
            <>
              <div className="info-box warning">
                <h4>{t('creator:workflow.stage5.notReady')}</h4>
                <p>
                  Your course does not meet the minimum requirements for publishing.
                </p>
                <ul className="requirements-list">
                  <li className={isValid ? 'met' : 'unmet'}>
                    {isValid ? '✓' : '✗'} Validation passed
                  </li>
                  <li className={overallScore >= 70 ? 'met' : 'unmet'}>
                    {overallScore >= 70 ? '✓' : '✗'} Minimum score of 70/100 (current: {overallScore})
                  </li>
                </ul>
                <p className="help-text">
                  Please go back to previous stages and improve your content, then re-run validation.
                </p>
              </div>
              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={() => onGoBackStage?.()}
                >
                  {t('creator:workflow.stage5.backToValidation')}
                </button>
                <button
                  className="btn-disabled"
                  disabled
                >
                  {t('creator:workflow.stage4.cannotProceed', { score: overallScore })}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Publish Modal — settings → loading → success/error, all in one overlay */}
      {showPublishModal && (
        <div className="publish-modal-overlay" onClick={() => !publishing && !publishSuccess && setShowPublishModal(false)}>
          <div className="publish-modal" onClick={(e) => e.stopPropagation()}>

            {/* State: Publishing in progress */}
            {publishing && (
              <div className="publish-modal-state publish-modal-loading">
                <div className="spinner"></div>
                <p className="loading-title">{t('creator:workflow.stage5.publishing')}</p>
                <p className="loading-subtitle">{t('creator:workflow.stage5.publishingDesc')}</p>
                <p className="loading-note">{t('creator:workflow.stage5.publishingNote')}</p>
              </div>
            )}

            {/* State: Publish success */}
            {!publishing && publishSuccess && (
              <div className="publish-modal-state publish-modal-success">
                <div className="success-icon">🎉</div>
                <h3>{t('creator:workflow.stage5.publishSuccess')}</h3>
                <div className="course-id-display">
                  <span className="label">{t('creator:workflow.stage5.courseId')}</span>
                  <code className="course-id">{publishSuccess.courseId}</code>
                </div>
                <div className="success-actions">
                  <button
                    className="btn-primary"
                    onClick={() => window.location.href = `/courses/${publishSuccess.courseId}`}
                  >
                    {t('creator:workflow.stage5.viewPublished')}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => window.location.href = '/creator/drafts'}
                  >
                    {t('creator:workflow.stage5.backToDrafts')}
                  </button>
                </div>
              </div>
            )}

            {/* State: Publish error */}
            {!publishing && publishError && (
              <div className="publish-modal-state publish-modal-error">
                <div className="error-icon">❌</div>
                <h3>{t('creator:workflow.stage5.publishFailed')}</h3>
                <pre className="error-message">{publishError}</pre>
                <div className="error-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setPublishError(null);
                      setShowPublishModal(false);
                    }}
                  >
                    {t('common:dismiss')}
                  </button>
                  <button
                    className="btn-success"
                    onClick={() => {
                      setPublishError(null);
                      handlePublish();
                    }}
                  >
                    {t('common:retry')}
                  </button>
                </div>
              </div>
            )}

            {/* State: Settings form (default) */}
            {!publishing && !publishSuccess && !publishError && (
              <>
                <div className="publish-modal-header">
                  <h3>{t('creator:workflow.stage5.publishSettings')}</h3>
                  <button
                    type="button"
                    className="publish-modal-close"
                    onClick={() => setShowPublishModal(false)}
                  >
                    ×
                  </button>
                </div>

                <div className="publish-modal-body">
                  {/* Permission */}
                  <div className="setting-group">
                    <label className="setting-label">{t('creator:workflow.stage5.permission')}</label>
                    <div className="permission-options permission-options-4">
                      <button
                        type="button"
                        className={`permission-option ${permission === 'public' ? 'active' : ''}`}
                        onClick={() => setPermission('public')}
                      >
                        <span className="permission-icon">🌐</span>
                        <span className="permission-text">
                          <strong>{t('creator:workflow.stage5.permissionPublic')}</strong>
                          <small>{t('creator:workflow.stage5.permissionPublicDesc')}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`permission-option ${permission === 'followers' ? 'active' : ''}`}
                        onClick={() => setPermission('followers')}
                      >
                        <span className="permission-icon">👥</span>
                        <span className="permission-text">
                          <strong>{t('creator:workflow.stage5.permissionFollowers')}</strong>
                          <small>{t('creator:workflow.stage5.permissionFollowersDesc')}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`permission-option ${permission === 'group' ? 'active' : ''}`}
                        onClick={() => setPermission('group')}
                      >
                        <span className="permission-icon">📋</span>
                        <span className="permission-text">
                          <strong>{t('creator:workflow.stage5.permissionGroup')}</strong>
                          <small>{t('creator:workflow.stage5.permissionGroupDesc')}</small>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`permission-option ${permission === 'private' ? 'active' : ''}`}
                        onClick={() => setPermission('private')}
                      >
                        <span className="permission-icon">🔒</span>
                        <span className="permission-text">
                          <strong>{t('creator:workflow.stage5.permissionPrivate')}</strong>
                          <small>{t('creator:workflow.stage5.permissionPrivateDesc')}</small>
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Followers info */}
                  {permission === 'followers' && (
                    <div className="setting-group">
                      <p className="setting-hint permission-info">{t('creator:workflow.stage5.followersInfo')}</p>
                    </div>
                  )}

                  {/* Group selector (only when group) */}
                  {permission === 'group' && (
                    <div className="setting-group group-selector-group">
                      <label className="setting-label">{t('creator:workflow.stage5.selectGroups')}</label>
                      <p className="setting-hint">{t('creator:workflow.stage5.selectGroupsHint')}</p>
                      {groupsLoading ? (
                        <p className="setting-hint">{t('common:loading')}</p>
                      ) : creatorGroups.length === 0 ? (
                        <div className="no-groups-message">
                          <p>{t('creator:workflow.stage5.noGroups')}</p>
                          <a href="/creator/groups" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-small">
                            {t('creator:workflow.stage5.manageGroups')}
                          </a>
                        </div>
                      ) : (
                        <>
                          <div className="group-checkbox-list">
                            {creatorGroups.map(group => (
                              <label key={group.id} className="group-checkbox-item">
                                <input
                                  type="checkbox"
                                  checked={selectedGroupIds.includes(group.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedGroupIds(prev => [...prev, group.id]);
                                    } else {
                                      setSelectedGroupIds(prev => prev.filter(id => id !== group.id));
                                    }
                                  }}
                                />
                                <span className="group-info">
                                  <strong>{group.name}</strong>
                                  {group.description && <small>{group.description}</small>}
                                  <small className="member-count">{group.memberCount || 0} {t('creator:workflow.stage5.members')}</small>
                                </span>
                              </label>
                            ))}
                          </div>
                          <a href="/creator/groups" target="_blank" rel="noopener noreferrer" className="manage-groups-link">
                            {t('creator:workflow.stage5.manageGroups')}
                          </a>
                        </>
                      )}
                    </div>
                  )}

                  {/* Whitelist (only when private) */}
                  {permission === 'private' && (
                    <div className="setting-group whitelist-group">
                      <label className="setting-label">{t('creator:workflow.stage5.whitelist')}</label>
                      <p className="setting-hint">{t('creator:workflow.stage5.whitelistHint')}</p>
                      <div className="whitelist-input-row">
                        <textarea
                          className="whitelist-input"
                          value={whitelistInput}
                          onChange={(e) => setWhitelistInput(e.target.value)}
                          placeholder={t('creator:workflow.stage5.whitelistPlaceholder')}
                          rows={2}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddEmails();
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn-secondary btn-add-emails"
                          onClick={handleAddEmails}
                        >
                          {t('creator:workflow.stage5.addEmails')}
                        </button>
                      </div>
                      {whitelistEmails.length > 0 && (
                        <div className="whitelist-tags">
                          {whitelistEmails.map(email => (
                            <span key={email} className="whitelist-tag">
                              {email}
                              <button
                                type="button"
                                className="whitelist-tag-remove"
                                onClick={() => handleRemoveEmail(email)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {whitelistEmails.length > 0 && (
                        <p className="whitelist-count">
                          {t('creator:workflow.stage5.whitelistCount', { count: whitelistEmails.length })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Tags (creator-managed content tags) */}
                  <div className="setting-group">
                    <label className="setting-label">{t('creator:workflow.stage5.tagsLabel')}</label>
                    <p className="setting-hint">{t('creator:workflow.stage5.tagsHint')}</p>
                    <TagChipInput
                      value={tags}
                      onChange={handleTagsChange}
                      suggestions={tagSuggestions}
                      maxTags={5}
                      placeholder={t('creator:workflow.stage5.tagsPlaceholder')}
                    />
                  </div>

                  {/* Scheduled Publish */}
                  <div className="setting-group">
                    <label className="setting-label-row">
                      <input
                        type="checkbox"
                        checked={useScheduledPublish}
                        onChange={(e) => setUseScheduledPublish(e.target.checked)}
                      />
                      <span>{t('creator:workflow.stage5.schedulePublish')}</span>
                    </label>
                    {useScheduledPublish && (
                      <div className="schedule-input-wrapper">
                        <input
                          type="datetime-local"
                          className="schedule-input"
                          value={scheduledPublishAt}
                          onChange={(e) => setScheduledPublishAt(e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                        />
                        <p className="setting-hint">{t('creator:workflow.stage5.scheduleHint')}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="copyright-acknowledge-section">
                  <label className="copyright-acknowledge-label">
                    <input
                      type="checkbox"
                      checked={copyrightAcknowledged}
                      disabled={acknowledgeSaving}
                      onChange={(e) => handleAcknowledgeChange(e.target.checked)}
                    />
                    <span>{t('creator:workflow.stage5.copyrightAcknowledge')}</span>
                  </label>
                </div>

                <div className="publish-modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPublishModal(false)}
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    className="btn-success btn-large"
                    onClick={handlePublish}
                    disabled={
                      loading ||
                      !copyrightAcknowledged ||
                      (useScheduledPublish && !scheduledPublishAt) ||
                      (permission === 'group' && selectedGroupIds.length === 0)
                    }
                  >
                    {useScheduledPublish && scheduledPublishAt
                      ? t('creator:workflow.stage5.scheduleAndSave')
                      : t('creator:workflow.stage5.confirmPublish')
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Stage5Publishing;
